/**
 * One skeleton per screen, each shaped like the screen it stands in for.
 *
 * They live together rather than inside their views for one reason: a skeleton is
 * a promise about a layout, and promises drift. Side by side it is obvious when
 * the home skeleton still shows four rows above a summary the real home moved.
 *
 * Each is built from the primitives in components/Skeleton and wrapped by its
 * caller in SkeletonScreen, which owns the pulse and the accessibility contract.
 * None of them takes real data — a skeleton that reads the store is no longer a
 * skeleton, it is a partial paint with grey bits.
 */

import {
  SkeletonBlock,
  SkeletonCard,
  SkeletonCircle,
  SkeletonLine,
  SkeletonRow,
} from '@/components/Skeleton';

/** Repeat a shape n times with stable keys. */
function repeat(count: number, render: (index: number) => JSX.Element) {
  return Array.from({ length: count }, (_, index) => render(index));
}

/**
 * The wallet-first home: the account switcher, the portfolio headline, the action
 * row, the chain chips, and the holdings list.
 *
 * Four rows because that is roughly a phone's worth — enough to fill the fold so
 * the page does not visibly grow when the real list lands, few enough that a
 * one-wallet account does not watch three phantom rows disappear.
 */
export function HomeSkeleton() {
  return (
    <>
      <div className="skeleton__row">
        <SkeletonCircle size="2.75rem" />
        <span className="skeleton__row-lines">
          <SkeletonLine width="45%" />
          <SkeletonLine width="30%" height="0.625rem" />
        </span>
      </div>

      {/* The portfolio headline: a label over the big total. */}
      <div className="skeleton__stack">
        <SkeletonLine width="30%" height="0.625rem" />
        <SkeletonLine width="60%" height="2rem" />
      </div>

      {/* The four action buttons. */}
      <SkeletonBlock height="3.5rem" />

      {/* The chain filter chips. */}
      <SkeletonBlock height="2rem" width="70%" />

      <div className="skeleton__stack">{repeat(4, (i) => <SkeletonRow key={i} />)}</div>
    </>
  );
}

/** A plain run of list rows, for Activity and any other single-list screen. */
export function ListSkeleton({ rows = 6 }: { rows?: number }) {
  return <div className="skeleton__stack">{repeat(rows, (i) => <SkeletonRow key={i} />)}</div>;
}

/** Activity: one date group followed by the compact two-column ledger rows. */
export function ActivitySkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="activity-skeleton">
      <div className="activity-skeleton__date">
        <SkeletonLine width="7.5rem" height="1.25rem" />
      </div>
      <div className="activity-skeleton__rows">
        {repeat(rows, (index) => (
          <div className="activity-skeleton__row" key={index}>
            <SkeletonCircle size="2.5rem" />
            <span className="activity-skeleton__identity">
              <SkeletonLine width={index % 3 === 0 ? '4.5rem' : '5.5rem'} />
              <SkeletonLine width="6.5rem" height="0.625rem" />
            </span>
            <span className="activity-skeleton__values">
              <SkeletonLine width={index % 2 === 0 ? '6.75rem' : '5.75rem'} />
              <SkeletonLine width="4.5rem" height="0.625rem" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Send step one: the lead line and the spendable-asset list. */
export function AssetListSkeleton() {
  return (
    <>
      <SkeletonLine width="55%" height="0.625rem" />
      <div className="skeleton__stack">{repeat(5, (i) => <SkeletonRow key={i} />)}</div>
    </>
  );
}

/** Receive: the subtitle, the wallet card, and the QR block under it. */
export function ReceiveSkeleton() {
  return (
    <>
      <SkeletonLine width="65%" height="0.625rem" />
      <SkeletonCard>
        <div className="skeleton__row">
          <SkeletonCircle size="2.25rem" />
          <span className="skeleton__row-lines">
            <SkeletonLine width="50%" />
            <SkeletonLine width="70%" height="0.625rem" />
          </span>
        </div>
        {/* The QR is square, so the placeholder is too — a rectangle here would
            resize the card the moment the code renders. */}
        <SkeletonBlock height="12.5rem" width="12.5rem" />
        <SkeletonLine width="100%" height="0.625rem" />
      </SkeletonCard>
    </>
  );
}

/** Swap: the two full-height token panels, their overlap control, settings, and CTA. */
export function SwapSkeleton() {
  return (
    <div className="swap-skeleton">
      <div className="swap-skeleton__form">
        {[0, 1].map((side) => (
          <div className="swap-skeleton__card" key={side}>
            <div className="swap-skeleton__top">
              <SkeletonLine width="4.5rem" />
              <SkeletonBlock width="9.5rem" height="2.25rem" />
            </div>
            <div className="swap-skeleton__main">
              <SkeletonLine width="3rem" height="2rem" />
              <SkeletonBlock width="8rem" height="2.75rem" />
            </div>
            <div className="swap-skeleton__bottom">
              <SkeletonLine width="3.5rem" height="0.625rem" />
              {side === 0 ? (
                <SkeletonLine width="8.5rem" height="0.625rem" />
              ) : null}
            </div>
          </div>
        ))}
        <span className="swap-skeleton__flip">
          <SkeletonCircle size="2.75rem" />
        </span>
      </div>
      <SkeletonLine width="8rem" />
      <SkeletonBlock height="4rem" />
    </div>
  );
}

/** Earn: the market table, then the account panel. */
export function EarnSkeleton() {
  return (
    <>
      <SkeletonLine width="65%" height="0.625rem" />
      <SkeletonCard>
        <SkeletonLine width="40%" />
        {repeat(3, (i) => (
          <SkeletonRow key={i} markSize="1.75rem" />
        ))}
      </SkeletonCard>
      <SkeletonCard>
        <SkeletonLine width="35%" />
        <SkeletonBlock height="4rem" />
      </SkeletonCard>
    </>
  );
}

/** A settings screen: a group heading over a few rows. */
export function SettingsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <>
      <SkeletonLine width="35%" height="0.625rem" />
      <SkeletonCard>
        {repeat(rows, (i) => (
          <SkeletonRow key={i} markSize="1.75rem" />
        ))}
      </SkeletonCard>
    </>
  );
}
